// src/pages/ClientDashboard.tsx

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserRole } from '../hooks/useUserRole';
import Navigation from '../components/Navigation';

const ClientDashboard: React.FC = () => {
  const { user, role, loading } = useUserRole();
  const navigate = useNavigate();

  useEffect(() => {
    // Redirect admin/staff to their respective dashboards
    if (!loading) {
      if (role === 'admin') {
        navigate('/admin-dashboard');
      } else if (role === 'staff') {
        navigate('/staff-dashboard');
      }
    }
  }, [loading, role, navigate]);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (role !== 'client') {
    return null; // Will redirect via useEffect
  }

  return (
    <div>
      <Navigation />
      <div style={{ padding: '2rem', maxWidth: '1200px', margin: '0 auto' }}>
        <h1>Client Dashboard</h1>
        <p style={{ color: '#666', marginBottom: '2rem' }}>
          Welcome, {user?.displayName || user?.email}. Manage your account and view your services.
        </p>

        {/* Dashboard Content */}
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <DashboardCard 
            title="Onboarding"
            icon="📝"
            description="Complete or update your onboarding information"
            onClick={() => navigate('/onboarding')}
          />
          <DashboardCard 
            title="Your Tasks"
            icon="📋"
            description="View and track your assigned tasks"
            onClick={() => navigate('/client-tasks')}
          />
          <DashboardCard 
            title="Documents"
            icon="📄"
            description="Access your document vault"
            onClick={() => navigate('/documents')}
          />
          <DashboardCard 
            title="Entity Formation"
            icon="🏢"
            description="View and manage your entity formation forms"
            onClick={() => navigate('/entity-formation')}
          />
        </div>

        {/* Quick Info */}
        <div style={{ 
          backgroundColor: '#f8f9fa', 
          padding: '1.5rem', 
          borderRadius: '8px'
        }}>
          <h3 style={{ marginTop: 0 }}>Quick Actions</h3>
          <ul style={{ listStyle: 'none', padding: 0 }}>
            <li style={{ padding: '0.5rem 0' }}>✅ View task progress</li>
            <li style={{ padding: '0.5rem 0' }}>📄 Upload additional documents</li>
            <li style={{ padding: '0.5rem 0' }}>💳 View subscription status</li>
            <li style={{ padding: '0.5rem 0' }}>🧾 Review reports & invoices</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

interface DashboardCardProps {
  title: string;
  icon: string;
  description: string;
  onClick: () => void;
}

const DashboardCard: React.FC<DashboardCardProps> = ({ title, icon, description, onClick }) => (
  <div 
    onClick={onClick}
    style={{
      backgroundColor: 'white',
      padding: '1.5rem',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      cursor: 'pointer',
      transition: 'transform 0.2s, box-shadow 0.2s',
      border: '1px solid #dee2e6'
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = '0 4px 8px rgba(0,0,0,0.15)';
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
    }}
  >
    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>{icon}</div>
    <h3 style={{ margin: '0 0 0.5rem 0' }}>{title}</h3>
    <p style={{ color: '#666', margin: 0 }}>{description}</p>
  </div>
);

export default ClientDashboard;
